import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { fmtDate, inr, STATUS_COLORS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_ADDR = { label: "Home", name: "", phone: "", pincode: "", line1: "", line2: "", city: "", state: "", is_default: false };

const COUNTRY_CODES = ["+91", "+1", "+44", "+971", "+65", "+61"];
const COUNTRY_LEN = { "+91": 10, "+1": 10, "+44": 10, "+971": 9, "+65": 8, "+61": 9 };

function splitE164(phone) {
  for (const cc of [...COUNTRY_CODES].sort((a, b) => b.length - a.length)) {
    if (phone.startsWith(cc)) return { cc, national: phone.slice(cc.length) };
  }
  return { cc: "+91", national: phone.replace(/\D/g, "") };
}

function WalletRewards() {
  const [wallet, setWallet] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/loyalty").then((r) => setLoyalty(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const redeem = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gift-cards/redeem", { code: redeemCode.trim() });
      toast.success(`${inr(r.data.credited)} added to your wallet`);
      setRedeemCode("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4 max-w-3xl" data-testid="wallet-tab">
      <div className="border border-neutral-200 p-6 bg-white">
        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">Store credit</p>
        <p className="font-heading text-3xl font-semibold mt-2" data-testid="wallet-balance">{wallet ? inr(wallet.balance) : "—"}</p>
        <p className="text-xs text-neutral-500 mt-1">Refunds and gift-card redemptions land here. Apply it at checkout.</p>
        <div className="flex gap-2 mt-4">
          <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="Gift card code" className="font-mono h-9 text-sm" data-testid="wallet-redeem-input" />
          <Button size="sm" variant="outline" onClick={redeem} disabled={busy} data-testid="wallet-redeem-btn">Redeem</Button>
        </div>
        {(wallet?.ledger || []).length > 0 && (
          <div className="mt-4 border-t border-neutral-100 pt-3 max-h-48 overflow-y-auto divide-y divide-neutral-100" data-testid="wallet-ledger">
            {wallet.ledger.map((e, i) => (
              <div key={i} className="py-1.5 text-xs flex justify-between gap-2">
                <span className="text-neutral-600 truncate">{e.reason}</span>
                <span className={`font-mono shrink-0 ${e.amount >= 0 ? "text-emerald-700" : "text-neutral-500"}`}>{e.amount >= 0 ? "+" : ""}{inr(e.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="border border-neutral-200 p-6 bg-white">
        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">Loyalty points</p>
        <p className="font-heading text-3xl font-semibold mt-2" data-testid="loyalty-balance">{loyalty ? loyalty.balance.toLocaleString("en-IN") : "—"} <span className="text-base text-neutral-400">pts</span></p>
        {loyalty?.enabled ? (
          <p className="text-xs text-neutral-500 mt-1">Earn {loyalty.earn_pct}% back as points on every paid order. Redeem from {loyalty.min_redeem.toLocaleString("en-IN")} pts at checkout.</p>
        ) : (
          <p className="text-xs text-neutral-500 mt-1">Loyalty programme is not active right now.</p>
        )}
        {(loyalty?.ledger || []).length > 0 && (
          <div className="mt-4 border-t border-neutral-100 pt-3 max-h-48 overflow-y-auto divide-y divide-neutral-100" data-testid="loyalty-ledger">
            {loyalty.ledger.map((e, i) => (
              <div key={i} className="py-1.5 text-xs flex justify-between gap-2">
                <span className="text-neutral-600 truncate">{e.reason}</span>
                <span className={`font-mono shrink-0 ${e.points >= 0 ? "text-emerald-700" : "text-neutral-500"}`}>{e.points >= 0 ? "+" : ""}{e.points.toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Account() {
  const { user, logout, reload } = useAuth();
  const { config } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [phoneCC, setPhoneCC] = useState("+91");
  const [phoneNum, setPhoneNum] = useState("");
  const [pvSent, setPvSent] = useState(false);
  const [pvCode, setPvCode] = useState("");
  const [pvBusy, setPvBusy] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    if (user === null) navigate("/login?next=/account");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || "", phone: user.phone || "" });
    const { cc, national } = splitE164(user.phone || "");
    setPhoneCC(cc);
    setPhoneNum(national);
    api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/auth/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const saveProfile = async () => {
    try {
      await api.put("/auth/profile", profile);
      await reload();
      toast.success("Profile updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePhone = async () => {
    try {
      await api.put("/auth/phone", { country_code: phoneCC, phone: phoneNum });
      setPvSent(false); setPvCode("");
      await reload();
      toast.success("Phone saved in international format");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const requestPhoneVerify = async () => {
    setPvBusy(true);
    try {
      const r = await api.post("/auth/phone/request-verification");
      setPvSent(true);
      if (r.data?.dev_otp) toast.info(`Test mode — your code is ${r.data.dev_otp}`);
      else toast.success(`Code sent to ${user.phone}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setPvBusy(false); }
  };

  const verifyPhone = async () => {
    setPvBusy(true);
    try {
      await api.post("/auth/phone/verify", { otp: pvCode });
      setPvSent(false); setPvCode("");
      await reload();
      toast.success("Phone number verified");
    } catch (e) { toast.error(errMsg(e)); } finally { setPvBusy(false); }
  };

  const saveAddress = async () => {
    try {
      if (editId) await api.put(`/auth/addresses/${editId}`, addrForm);
      else await api.post("/auth/addresses", addrForm);
      const r = await api.get("/auth/addresses");
      setAddresses(r.data);
      setAddrOpen(false);
      setEditId(null);
      setAddrForm(EMPTY_ADDR);
      toast.success("Address saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteAddress = async (id) => {
    await api.delete(`/auth/addresses/${id}`);
    setAddresses((a) => a.filter((x) => x.id !== id));
    toast.success("Address removed");
  };

  return (
    <div className="container-x py-10" data-testid="account-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="account-greeting">Hi, {user.name}</h1>
          <p className="text-sm text-neutral-500 font-mono mt-1">{user.email}</p>
        </div>
        <div className="flex gap-2">
          {user.role === "admin" && (
            <Button variant="outline" onClick={() => navigate("/admin")} data-testid="goto-admin-btn">Admin Panel</Button>
          )}
          <Button variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">Log Out</Button>
        </div>
      </div>

      <Tabs defaultValue={params.get("tab") || "orders"} className="mt-8">
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses">Addresses</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet">Wallet & Rewards</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          {orders.length === 0 ? (
            <div className="border border-neutral-200 p-10 text-center text-neutral-500" data-testid="no-orders">
              Your orders will appear here once you have placed one. <Link to="/shop" className="underline">Start shopping</Link>.
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/account/orders/${o.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 border border-neutral-200 p-4 hover:border-neutral-900 transition-colors"
                  data-testid={`order-row-${o.order_number}`}
                >
                  <div>
                    <p className="font-mono font-semibold text-sm">{o.order_number}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{fmtDate(o.created_at)} · {o.items?.length} item(s)</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 ${STATUS_COLORS[o.status] || "bg-neutral-100"}`} data-testid={`order-status-${o.order_number}`}>
                      {o.status}
                    </span>
                    <span className="font-heading font-semibold">{inr(o.total)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="addresses" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {addresses.map((a) => (
              <div key={a.id} className="border border-neutral-200 p-4 relative" data-testid={`address-card-${a.id}`}>
                {a.is_default && <span className="absolute top-3 right-3 text-[10px] font-mono bg-neutral-900 text-white px-2 py-0.5">DEFAULT</span>}
                <p className="font-medium text-sm">{a.name} <span className="text-xs font-mono text-neutral-400">({a.label})</span></p>
                <p className="text-sm text-neutral-600 mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                <p className="text-sm text-neutral-600">{a.city} — {a.pincode}, {a.state}</p>
                <p className="text-xs font-mono text-neutral-500 mt-1">{a.phone}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => { setAddrForm({ ...EMPTY_ADDR, ...a }); setEditId(a.id); setAddrOpen(true); }} data-testid={`address-edit-${a.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteAddress(a.id)} data-testid={`address-delete-${a.id}`}>Delete</Button>
                </div>
              </div>
            ))}
            <button
              onClick={() => { setAddrForm(EMPTY_ADDR); setEditId(null); setAddrOpen(true); }}
              className="border border-dashed border-neutral-300 p-4 text-sm text-neutral-500 hover:border-neutral-900 hover:text-neutral-900 transition-colors min-h-32"
              data-testid="add-address-btn"
            >
              + Add New Address
            </button>
          </div>
        </TabsContent>

        <TabsContent value="wallet" className="mt-4">
          <WalletRewards />
        </TabsContent>
        <TabsContent value="profile" className="mt-4 max-w-md">
          <div className="border border-neutral-200 p-6 space-y-4">
            <div><Label htmlFor="pf-name">Full Name</Label><Input id="pf-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" /></div>
            <div>
              <Label htmlFor="pf-phone">Mobile (for delivery)</Label>
              <div className="flex gap-2">
                <select value={phoneCC} onChange={(e) => { setPhoneCC(e.target.value); setPhoneNum(""); }}
                  className="h-10 w-24 border border-neutral-200 rounded-sm px-2 text-sm bg-white shrink-0" data-testid="profile-country-select">
                  {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input id="pf-phone" value={phoneNum} onChange={(e) => setPhoneNum(e.target.value.replace(/\D/g, "").slice(0, COUNTRY_LEN[phoneCC] || 12))}
                  placeholder={`${COUNTRY_LEN[phoneCC] || 10}-digit mobile`} className="font-mono" data-testid="profile-phone-input" />
              </div>
              {user.phone && (
                <p className="text-[11px] font-mono mt-1" data-testid="phone-verified-status">
                  {user.phone} — {user.phone_verified
                    ? <span className="text-emerald-700">VERIFIED</span>
                    : <span className="text-amber-700">NOT VERIFIED (captured for delivery only)</span>}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={saveProfile} className="bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="profile-save-btn">Save Profile</Button>
              <Button variant="outline" onClick={savePhone} data-testid="phone-save-btn">Save Phone</Button>
            </div>
            {user.phone && !user.phone_verified && config?.auth_otp_channel !== "email" && (
              <div className="border border-neutral-200 p-4 space-y-3" data-testid="phone-verify-block">
                <p className="text-xs text-neutral-600">Verify this number so it can receive order updates by {config?.auth_otp_channel === "whatsapp" ? "WhatsApp" : "SMS"}.</p>
                {!pvSent ? (
                  <Button variant="outline" onClick={requestPhoneVerify} disabled={pvBusy} data-testid="phone-verify-request-btn">Send verification code</Button>
                ) : (
                  <div className="flex gap-2">
                    <Input value={pvCode} onChange={(e) => setPvCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="font-mono" data-testid="phone-verify-code-input" />
                    <Button onClick={verifyPhone} disabled={pvBusy} className="bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="phone-verify-confirm-btn">Verify</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby={undefined} data-testid="address-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} Address</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label</Label><Input value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} data-testid="addr-label-input" /></div>
            <div><Label>Name</Label><Input value={addrForm.name} onChange={(e) => setAddrForm({ ...addrForm, name: e.target.value })} data-testid="addr-name-input" /></div>
            <div><Label>Phone</Label><Input value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="addr-phone-input" /></div>
            <div><Label>PIN Code</Label><Input value={addrForm.pincode} onChange={(e) => setAddrForm({ ...addrForm, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="addr-pincode-input" /></div>
            <div className="col-span-2"><Label>Address Line 1</Label><Input value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} data-testid="addr-line1-input" /></div>
            <div className="col-span-2"><Label>Address Line 2</Label><Input value={addrForm.line2} onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })} data-testid="addr-line2-input" /></div>
            <div><Label>City</Label><Input value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} data-testid="addr-city-input" /></div>
            <div><Label>State</Label><Input value={addrForm.state} onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} data-testid="addr-state-input" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={addrForm.is_default} onChange={(e) => setAddrForm({ ...addrForm, is_default: e.target.checked })} data-testid="addr-default-check" />
              Set as default address
            </label>
          </div>
          <Button onClick={saveAddress} className="w-full mt-2 bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="addr-save-btn">Save Address</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
